import React, { useState } from "react";
import { Button, Modal, Textarea } from "../../../../components/core";
import { useApolloClient, useMutation } from "@apollo/client";
import { Dialog } from "primereact/dialog";
import { addHoursToDate } from "../../../../algorithms/date";
import { SAVE_STATUS_HISTORY } from "../../../../constants/graphql/partner-agent-queries";
import axios from "axios";
import ConfirmationDialog from "./confirmation-dialog";

const STATUS_OPTIONS = [
  {
    value: 1,
    label: "Active",
    color: "text-green-700",
    bg: "bg-green-50",
    border: "border-green-200",
    dot: "bg-green-500",
  },
  {
    value: 0,
    label: "Suspended",
    color: "text-orange-600",
    bg: "bg-orange-50",
    border: "border-orange-200",
    dot: "bg-orange-500",
  },
  {
    value: -1,
    label: "Blocked",
    color: "text-red-600",
    bg: "bg-red-50",
    border: "border-red-200",
    dot: "bg-red-500",
  },
];

const operatorId = localStorage.getItem("user")
  ? JSON.parse(localStorage.getItem("user")).operator_id
  : "";

export default function EditAgentStatusModal({
  isOpen,
  onClose,
  agentData,
  onSuccess,
  forWho = "users", // "users" | "agent"
}) {
  const client = useApolloClient();
  const [selectedStatus, setSelectedStatus] = useState(null);
  const [suspensionHours, setSuspensionHours] = useState("");
  const [msgToAgent, setMsgToAgent] = useState("");
  const [msgToUser, setMsgToUser] = useState("");

  const [privateNote, setPrivateNote] = useState("");
  const [openConfirmation, setOpenConfirmation] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [validation, setValidation] = useState({ open: false });

  const [saveStatusHistory] = useMutation(SAVE_STATUS_HISTORY);

  const isSuspended = selectedStatus === 0;

  // Determine the current status label to filter it out of options
  const currentStatusNum = Number(agentData.agentStatus);
  const isCurrentlySuspendedOrBlocked =
    currentStatusNum === 0 || currentStatusNum === -1;

  const filteredOptions = STATUS_OPTIONS;

  const selectedOption = STATUS_OPTIONS.find((s) => s.value === selectedStatus);

  const defaultMessage =
    selectedStatus === 1
      ? "Your MBOX courier account has been activated."
      : selectedStatus === 0
        ? `Your MBOX courier account has been suspended until ${addHoursToDate(Number(suspensionHours))}.`
        : "Your MBOX courier account has been blocked.";

  const defaultPrivateNote =
    selectedStatus === 1
      ? ""
      : selectedStatus === 0
        ? `Suspended until ${addHoursToDate(Number(suspensionHours))}`
        : "Blocked";

  const showError = (message) =>
    setValidation({
      open: true,
      icon: `${process.env.PUBLIC_URL}/assets/images/error.png`,
      title: "Error",
      message: message || "Failed to update agent status. Please try again.",
      actions: (
        <Button
          value="OK"
          className="p-2 w-full"
          onClick={() => setValidation({ open: false })}
        />
      ),
    });

  const handleClose = () => {
    setSelectedStatus(null);
    setSuspensionHours("");
    setMsgToAgent("");
    setMsgToUser("");
    setPrivateNote("");
    setOpenConfirmation(false);
    onClose?.();
  };

  const handleProceed = () => {
    if (selectedStatus === null) return;
    if (isSuspended && !suspensionHours) return;
    setOpenConfirmation(true);
  };

  const handleConfirm = async ({
    message: confirmedMessage,
    privateNote: confirmedPrivateNote,
  } = {}) => {
    const effectiveStatus = selectedStatus === 0 ? -1 : selectedStatus;
    const finalMessage = confirmedMessage || defaultMessage;
    const finalPrivateNote = confirmedPrivateNote;

    const apiLink = `${process.env.REACT_APP_website_api}v1/website/operator/agent/status/change`;

    setIsLoading(true);
    try {
      const [response] = await Promise.all([
        axios.post(apiLink, {
          operator_id: operatorId,
          partner_agent_id: agentData.partnerAgentId,
          partner_id: agentData.partnerId,
          agent_mobile: agentData.agentMobile,
          partner_branch_id: agentData.partnerBranchId,
          agent_status: effectiveStatus,
          status_duration: isSuspended ? Number(suspensionHours) : 0,
          agent_status_comments: finalMessage,
          internal_comments: finalPrivateNote,
        }),
        saveStatusHistory({
          variables: {
            agentStatusHistoryArgs: [
              {
                partnerAgentId: parseInt(agentData.partnerAgentId),
                agentStatus: effectiveStatus,
                verifiedStatus: agentData.verifiedStatus,
                agentStatusComments: finalMessage,
                comments: finalPrivateNote,
              },
            ],
          },
        }),
      ]);

      if (response.data.d === 1 && response.data.s.c === 200) {
        setOpenConfirmation(false);
        setValidation({
          open: true,
          icon: `${process.env.PUBLIC_URL}/assets/images/check.png`,
          title: "Success",
          message: "Agent status has been updated successfully.",
          actions: (
            <Button
              value="OK"
              className="p-2 w-full"
              onClick={async () => {
                setValidation({ open: false });
                await client.clearStore();
                handleClose();
                onSuccess?.();
              }}
            />
          ),
        });
      } else {
        showError();
      }
    } catch (err) {
      console.error("Error updating agent status:", err);
      showError(err.message);
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <>
      <Dialog
        visible={isOpen}
        onHide={handleClose}
        header="Edit Agent Status"
        draggable={false}
        resizable={false}
        className="max-w-[95%] md:w-[520px]"
        footer={
          <div className="flex justify-center gap-3 pt-2">
            <Button
              value="Cancel"
              className="px-6 py-2 bg-white border border-primary-primaryYellow rounded-md font-semibold text-sm"
              onClick={handleClose}
            />
            <Button
              value="Proceed"
              className="px-6 py-2 bg-primary-primaryYellow rounded-md font-semibold text-sm"
              onClick={handleProceed}
              disabled={
                selectedStatus === null || (isSuspended && !suspensionHours)
              }
            />
          </div>
        }
      >
        <div className="flex flex-col gap-5 pt-2">
          {/* Status pill selector */}
          <div className="flex flex-col gap-2">
            <label className="text-xs font-semibold uppercase tracking-wide text-gray-500">
              New Status <span className="text-red-500">*</span>
            </label>
            <div className="flex gap-2 flex-wrap">
              {filteredOptions.map((opt) => (
                <button
                  key={opt.value}
                  type="button"
                  onClick={() => setSelectedStatus(opt.value)}
                  className={`flex items-center gap-2 px-3 py-2 rounded-lg border transition-all text-sm font-medium ${
                    selectedStatus === opt.value
                      ? `${opt.bg} ${opt.border} ${opt.color} shadow-sm`
                      : "bg-white border-gray-200 text-gray-500 hover:bg-gray-50"
                  }`}
                >
                  <span
                    className={`w-2 h-2 rounded-full shrink-0 ${
                      selectedStatus === opt.value ? opt.dot : "bg-gray-300"
                    }`}
                  />
                  {opt.label}
                </button>
              ))}
            </div>
          </div>

          {/* Suspension Duration */}
          {isSuspended && (
            <div className="flex flex-col gap-1.5">
              <label className="text-xs font-semibold uppercase tracking-wide text-gray-500">
                Suspension Duration (hours){" "}
                <span className="text-red-500">*</span>
              </label>
              <input
                type="text"
                inputMode="numeric"
                maxLength={4}
                value={suspensionHours}
                onChange={(e) => {
                  const val = e.target.value;
                  if (/^\d*$/.test(val)) setSuspensionHours(val);
                }}
                placeholder="e.g. 24"
                className="border border-gray-300 rounded-md px-3 py-2 text-sm w-full focus:outline-none focus:ring-1 focus:ring-primary-primaryYellow"
              />
              <p className="text-xs text-gray-500 italic">
                Number of hours the agent will be suspended.
              </p>
            </div>
          )}
        </div>
      </Dialog>

      <ConfirmationDialog
        open={openConfirmation}
        onClose={() => setOpenConfirmation(false)}
        rejectionReasons={null}
        onConfirm={handleConfirm}
        variant={
          selectedStatus === 1
            ? "accept"
            : selectedStatus === 0
              ? "warning"
              : "reject"
        }
        title={`Set ${forWho === "agent" ? "Agent" : "User"} to ${selectedOption?.label ?? ""}`}
        subtitle="Please review the message below before confirming."
        messageLabel={`Message to ${forWho === "agent" ? "agent" : "user"}`}
        defaultMessage={msgToUser}
        defaultFallbackMessage={
          selectedStatus === 1
            ? `Your MBOX ${forWho === "user" ? "user" : "courier"} account has been activated.`
            : selectedStatus === 0
              ? `Your MBOX ${forWho === "user" ? "user" : "courier"} account has been suspended until ${addHoursToDate(Number(suspensionHours))}.`
              : `Your MBOX ${forWho === "user" ? "user" : "courier"} account has been blocked.`
        }
        showPrivateNote={true}
        confirmLabel={`Confirm`}
        requireMessage={isSuspended}
        messagePlaceholder={
          isSuspended
            ? "Please provide a reason for suspending this account..."
            : "Add a message..."
        }
        isSaving={isLoading}
        defaultFallbackPrivateNote={defaultPrivateNote}
      />

      {/* Confirmation dialog */}
      {/* <Dialog
        visible={openConfirmation}
        onHide={() => setOpenConfirmation(false)}
        header={`Set Agent to ${selectedOption?.label ?? ""}`}
        draggable={false}
        resizable={false}
        className="max-w-[95%] md:w-[520px]"
        footer={
          <div className="flex justify-center gap-3 pt-2">
            <Button
              value="Cancel"
              className="px-6 py-2 bg-white border border-primary-primaryYellow rounded-md font-semibold text-sm"
              onClick={() => setOpenConfirmation(false)}
              disabled={isLoading}
            />
            <Button
              value={isLoading ? "Saving..." : "Confirm"}
              className="px-6 py-2 bg-primary-primaryYellow rounded-md font-semibold text-sm"
              onClick={handleConfirm}
              disabled={isLoading}
            />
          </div>
        }
      >
        <div className="flex flex-col gap-4 pt-2">
          <p className="text-sm text-gray-500">
            Please review the message below before confirming.
          </p>

          <div className="flex flex-col gap-1.5">
            <label className="text-xs font-semibold uppercase tracking-wide text-gray-500">
              Message to Agent
            </label>
            <Textarea
              value={msgToAgent}
              onChange={(e) => setMsgToAgent(e.target.value)}
              name="msgToAgent"
              placeholder={defaultMessage}
              maxLength={1000}
            />
            <p className="text-xs text-gray-400 italic">
              Leave blank to use default message.
            </p>
          </div>

          <div className="flex flex-col gap-1.5">
            <label className="text-xs font-semibold uppercase tracking-wide text-gray-500">
              Private Comments / Reason
            </label>
            <Textarea
              value={privateNote}
              onChange={(e) => setPrivateNote(e.target.value)}
              name="privateNote"
              placeholder={defaultPrivateNote || "Internal note..."}
              maxLength={1000}
            />
            <p className="text-xs text-gray-400 italic">
              Leave blank to use default note.
            </p>
          </div>
        </div>
      </Dialog> */}

      <Modal
        open={validation.open}
        icon={validation.icon}
        title={validation.title}
        message={validation.message}
        actions={validation.actions}
      />
    </>
  );
}
